import React, { useState } from "react";
import { Box, Grid, Skeleton } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { WifiTetheringError } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import gitlabSelfManaged from "src/assets/icons/gitlab-self-hosted";
import { useAuth } from "src/components/providers/auth-context";
import notion from "src/assets/icons/notion";
import slack from "src/assets/icons/slack";
import gitlab from "src/assets/icons/gitlab";
import jira from "src/assets/icons/jira";
import googleWorkspace from "src/assets/icons/google-workspace";
import github from "src/assets/icons/github";
import Terms from "src/components/terms";
import fetchStatus from "src/utils/api/fetch-status";
import microsoft from "src/assets/icons/microsoft";
import WizardIntegrationButton from "src/scenes/setup-wizard/wizard-integration-button";
import IntegrationButton from "src/scenes/integrations/integration-button";
import styled from "styled-components";

const Title = styled.p`
    margin: 8px;
`;

const Subtitle = styled.p`
    font-size: 12px;
    margin: 8px;
`;

const svgs = {
    slack,
    jira,
    gitlab,
    google: googleWorkspace,
    github,
    gitlabSelfManaged,
    microsoft,
    notion,
};

const EnterpriseIntegrationsSection = ({ userIntegrations }) => {
    const [orgLevelIntegrations] = useState(["github", "jira", "notion", "gitlabSelfManaged"]);
    const [termsError, setTermsError] = useState(false);
    const [selectedIntegrationOrg, setSelectedIntegrationOrg] = useState();
    const [checkedTerms, setCheckedTerms] = useState(false);
    const [loading, setLoading] = useState(false);
    const { isDemo } = useAuth();
    const navigate = useNavigate();

    React.useEffect(() => {
        if (
            userIntegrations.status === fetchStatus.PENDING ||
            userIntegrations.status === fetchStatus.REFETCHING
        ) {
            setLoading(true);
        } else {
            setLoading(false);
        }
    }, [userIntegrations.status]);

    const handleTermsChange = (event) => {
        setCheckedTerms(event.target.checked);
        setTermsError(false);
    };

    const handleIntegration = (clickedIntegration) => () => {
        if (termsError) setTermsError(false);
        const { integrations } = userIntegrations.data;
        const selected = integrations.find(
            (integration) => integration.name === clickedIntegration
        );
        setSelectedIntegrationOrg(selected);
    };

    React.useEffect(() => {
        if (userIntegrations.data && selectedIntegrationOrg) {
            handleIntegration(selectedIntegrationOrg.name)();
        }
    }, [userIntegrations.data]);

    const handleConnect = async () => {
        if (!selectedIntegrationOrg.connected && !selectedIntegrationOrg.centrallyManaged) {
            if (!checkedTerms) return setTermsError(true);
            if (selectedIntegrationOrg.name === "github")
                navigate("../integrations/permissions/github-app", { replace: true });
            if (selectedIntegrationOrg.name === "jira")
                navigate("../integrations/permissions/jira-org", { replace: true });
            if (selectedIntegrationOrg.name === "notion")
                navigate("../integrations/permissions/notion-org", { replace: true });
            if (selectedIntegrationOrg.name === "gitlabSelfManaged")
                navigate("../integrations/permissions/gitlab-self-hosted-org", { replace: true });
        }
        if (
            selectedIntegrationOrg.connected ||
            (!selectedIntegrationOrg.connected && selectedIntegrationOrg.centrallyManaged)
        ) {
            if (selectedIntegrationOrg.name === "github")
                navigate("../integrations/settings/github-app", {
                    replace: true,
                });
            if (selectedIntegrationOrg.name === "jira")
                navigate("../integrations/settings/jira-org", {
                    replace: true,
                });
            if (selectedIntegrationOrg.name === "notion")
                navigate("../integrations/settings/notion-org", {
                    replace: true,
                });
            if (selectedIntegrationOrg.name === "gitlabSelfManaged")
                navigate("../integrations/settings/gitlab-self-hosted-org", {
                    replace: true,
                });
        }
        return undefined;
    };

    const renderOrgSkeletons = () => {
        return [0, 1, 2, 3].map((i) => (
            <Grid item key={i}>
                <Skeleton>
                    <WizardIntegrationButton />
                </Skeleton>
            </Grid>
        ));
    };

    const renderError = () => {
        return [0].map((i) => (
            <Grid container item key={i} alignItems="center" direction="column">
                <Grid item>
                    <WifiTetheringError fontSize="large" />
                </Grid>
                <Grid item>Error retrieving data</Grid>
            </Grid>
        ));
    };

    const isDisabled = ({ name, connected, integrationType, centrallyManaged }) => {
        if (name === "github") return connected && integrationType !== "app" && !centrallyManaged;
        if (name === "jira") return connected && !centrallyManaged;
        if (name === "notion") return connected && !centrallyManaged;
        if (name === "gitlabSelfManaged") return connected && !centrallyManaged;
        return false;
    };

    const renderOrganizationIntegrations = () => {
        if (userIntegrations.status === fetchStatus.PENDING) return renderOrgSkeletons();
        if (userIntegrations.status === fetchStatus.ERROR) return renderError();
        const { integrations } = userIntegrations.data;
        const current = integrations
            .filter(({ name }) => orgLevelIntegrations.includes(name))
            .map(
                ({
                    name,
                    connected,
                    centrallyManaged,
                    invalid,
                    invalidationReason,
                    pendingInstallations,
                    integrationType,
                }) => (
                    <Grid item>
                        <IntegrationButton
                            svg={svgs[name]}
                            checked={selectedIntegrationOrg && selectedIntegrationOrg.name === name}
                            connected={connected}
                            centrallyManaged={centrallyManaged}
                            invalid={invalid}
                            pendingInstallations={pendingInstallations}
                            invalidationReason={invalidationReason}
                            integrationType={integrationType}
                            action={handleIntegration(name)}
                            disabled={isDisabled({
                                name,
                                connected,
                                integrationType,
                                centrallyManaged,
                            })}
                        />
                    </Grid>
                )
            );
        return current;
    };

    return (
        <Box my={4.5} mx={4.5}>
            <Grid container spacing={5} direction="column">
                <Title>Step 1 - Company wide integrations</Title>
                <Subtitle>
                    Use the company wide integrations to integrate centrally with a tool.
                    <br /> This will allow all following users to see their data without having to
                    integrate themselves.
                </Subtitle>
                <Grid
                    container
                    item
                    spacing={2}
                    justifyContent="flex-start"
                    pt={0}
                    style={{ paddingTop: "16px" }}
                >
                    {renderOrganizationIntegrations()}
                </Grid>
                {selectedIntegrationOrg &&
                    !selectedIntegrationOrg.connected &&
                    !selectedIntegrationOrg.centrallyManaged && (
                        <Grid item alignItems="center" style={{ display: "flex" }}>
                            <Terms
                                checkedTerms={checkedTerms}
                                handleTermsChange={handleTermsChange}
                                termsError={termsError}
                            />
                        </Grid>
                    )}
                {selectedIntegrationOrg && !isDemo && (
                    <Grid item>
                        <LoadingButton
                            variant="contained"
                            color={
                                (selectedIntegrationOrg &&
                                    selectedIntegrationOrg.connected &&
                                    !selectedIntegrationOrg.centrallyManaged) ||
                                (selectedIntegrationOrg &&
                                    selectedIntegrationOrg.connected &&
                                    selectedIntegrationOrg.centrallyManaged) ||
                                (selectedIntegrationOrg &&
                                    !selectedIntegrationOrg.connected &&
                                    selectedIntegrationOrg.centrallyManaged)
                                    ? "secondary"
                                    : "primary"
                            }
                            onClick={handleConnect}
                            loading={loading}
                            disabled={!selectedIntegrationOrg}
                        >
                            {(selectedIntegrationOrg &&
                                selectedIntegrationOrg.connected &&
                                !selectedIntegrationOrg.centrallyManaged) ||
                            (selectedIntegrationOrg &&
                                selectedIntegrationOrg.connected &&
                                selectedIntegrationOrg.centrallyManaged) ||
                            (selectedIntegrationOrg &&
                                !selectedIntegrationOrg.connected &&
                                selectedIntegrationOrg.centrallyManaged)
                                ? "Settings"
                                : "Connect"}
                        </LoadingButton>
                    </Grid>
                )}
            </Grid>
        </Box>
    );
};

export default EnterpriseIntegrationsSection;
